/**
 * Add/Edit Custom App Modal
 * Form for creating or updating a user custom app (My Apps).
 * Includes an optional category picker with inline create-category support.
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '../../shared/components/ui';
import { LayoutGrid, Plus, Tag } from 'lucide-react';
import { isValidUserExternalUrl } from '../../utils/urlValidator';
import { useMyCategories, useCreateCategory } from '../../hooks';
import type {
  UserCustomApp,
  CreateUserCustomAppDto,
  UserCustomAppCategory,
} from '../../types';

const DEFAULT_COLOR = '#c00035';
const HEX_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

export interface AddEditCustomAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (dto: CreateUserCustomAppDto) => void;
  isLoading?: boolean;
  editApp: UserCustomApp | null; // null = add mode
}

// ---------------------------------------------------------------------------
// Inline "Create category" mini-form
// ---------------------------------------------------------------------------

interface CreateCategoryInlineProps {
  onCreated: (cat: UserCustomAppCategory) => void;
  onCancel: () => void;
}

const DEFAULT_CAT_COLOR = '#c00035';

const CreateCategoryInline: React.FC<CreateCategoryInlineProps> = ({ onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_CAT_COLOR);
  const createMutation = useCreateCategory();

  const handleSave = () => {
    if (!name.trim()) return;
    createMutation.mutate(
      { name: name.trim(), color: color || undefined },
      {
        onSuccess: (cat) => {
          onCreated(cat);
          setName('');
          setColor(DEFAULT_CAT_COLOR);
        },
      },
    );
  };

  return (
    <div className="mt-2 p-3 border border-yellow-200 rounded-lg bg-yellow-50 space-y-2">
      <p className="text-xs font-semibold text-yellow-700">New category</p>
      <div className="flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Category name"
          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
        />
        <div className="flex items-center gap-1">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            title="Pick a color"
            className="h-8 w-8 rounded border border-gray-300 cursor-pointer p-0.5"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!name.trim() || createMutation.isPending}
          className="px-3 py-1 text-xs bg-yellow-400 text-gray-900 font-medium rounded-lg hover:bg-yellow-500 disabled:opacity-50"
        >
          {createMutation.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export const AddEditCustomAppModal: React.FC<AddEditCustomAppModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  editApp,
}) => {
  const isEdit = !!editApp;
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [imageUrl, setImageUrl] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: categories = [] } = useMyCategories();

  useEffect(() => {
    if (isOpen) {
      if (editApp) {
        setName(editApp.name);
        setUrl(editApp.url);
        setDescription(editApp.description || '');
        setColor(editApp.color || DEFAULT_COLOR);
        setImageUrl(editApp.imageUrl || '');
        setDisplayOrder(editApp.displayOrder ?? 0);
        setCategoryId(editApp.categoryId ?? null);
      } else {
        setName('');
        setUrl('');
        setDescription('');
        setColor(DEFAULT_COLOR);
        setImageUrl('');
        setDisplayOrder(0);
        setCategoryId(null);
      }
      setShowCreateCategory(false);
      setErrors({});
    }
  }, [isOpen, editApp]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Name is required';
    if (!url.trim()) next.url = 'URL is required';
    else if (!isValidUserExternalUrl(url)) next.url = 'Please enter a valid http or https URL';
    if (imageUrl.trim() && !isValidUserExternalUrl(imageUrl)) {
      next.imageUrl = 'Please enter a valid http or https URL for the image';
    }
    if (color.trim() && !HEX_REGEX.test(color)) next.color = 'Enter a valid hex color (e.g. #c00035)';
    const orderNum = Number(displayOrder);
    if (Number.isNaN(orderNum) || orderNum < 0 || !Number.isInteger(orderNum)) {
      next.displayOrder = 'Display order must be a non-negative whole number';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || isLoading) return;
    onSubmit({
      name: name.trim(),
      url: url.trim(),
      description: description.trim() || undefined,
      color: color.trim() || undefined,
      imageUrl: imageUrl.trim() || undefined,
      displayOrder: Number(displayOrder) || 0,
      categoryId: categoryId ?? null,
    });
    onClose();
  };

  const handleCategoryCreated = (cat: UserCustomAppCategory) => {
    setCategoryId(cat.id);
    setShowCreateCategory(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit app' : 'Add app'}
      subtitle={isEdit ? 'Update your custom app link' : 'Add an external app link (opens in new tab)'}
      icon={<LayoutGrid size={24} />}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Monday Form"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {errors.name && <p className="mt-1 text-sm text-primary-600">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {errors.url && <p className="mt-1 text-sm text-primary-600">{errors.url}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color (optional)</label>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#c00035"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
            />
          </div>
          {errors.color && <p className="mt-1 text-sm text-primary-600">{errors.color}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image / Logo URL (optional)</label>
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {errors.imageUrl && <p className="mt-1 text-sm text-primary-600">{errors.imageUrl}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Display order (optional)</label>
          <input
            type="number"
            min={0}
            step={1}
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
            placeholder="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="mt-0.5 text-xs text-gray-500">Lower numbers appear first. 0 is first.</p>
          {errors.displayOrder && <p className="mt-1 text-sm text-primary-600">{errors.displayOrder}</p>}
        </div>

        {/* Category picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <span className="flex items-center gap-1.5">
              <Tag size={13} className="text-gray-400" />
              Category (optional)
            </span>
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <select
                value={categoryId ?? ''}
                onChange={(e) => setCategoryId(e.target.value === '' ? null : Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-sm appearance-none pr-8"
              >
                <option value="">— No category —</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {/* Show color dot for selected category */}
              {categoryId !== null && (() => {
                const selected = categories.find((c) => c.id === categoryId);
                return selected?.color ? (
                  <span
                    className="absolute right-8 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border border-white shadow-sm pointer-events-none"
                    style={{ backgroundColor: selected.color }}
                  />
                ) : null;
              })()}
            </div>
            <button
              type="button"
              onClick={() => setShowCreateCategory((v) => !v)}
              title="Create new category"
              className="flex items-center gap-1 px-2.5 py-2 border border-gray-300 rounded-lg text-gray-500 hover:text-indigo-600 hover:border-indigo-400 transition-colors text-sm"
            >
              <Plus size={14} />
            </button>
          </div>

          {showCreateCategory && (
            <CreateCategoryInline
              onCreated={handleCategoryCreated}
              onCancel={() => setShowCreateCategory(false)}
            />
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            {isLoading ? 'Saving...' : isEdit ? 'Update' : 'Add app'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddEditCustomAppModal;
